import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Col,
  Spinner,
  Badge,
} from "reactstrap";
import moment from "moment";
import OnlyHeader from "components/Layout/OnlyHeader.js";
import { useDispatch, useSelector } from "react-redux";
import { getPlatFormFeedbacks } from "store/actions/restaurantAction";

const PlatformFeedback = () => {
  const dispatch = useDispatch();
  const [loadCount, setLoadCount] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);

  const { feedbackLoader, platform_feedbacks, hasMoreFeedbacks } = useSelector(
    (state) => state.restaurant
  );

  const handleLoadMore = () => {
    setLoadingMore(true);
    const updatedCount = loadCount + 10;
    setLoadCount(updatedCount);
    dispatch(getPlatFormFeedbacks(updatedCount)).finally(() =>
      setLoadingMore(false)
    );
  };

  useEffect(() => {
    dispatch(getPlatFormFeedbacks(loadCount));
  }, [dispatch, loadCount]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row className="justify-content-center">
          <Col lg="10" xl="12">
            <Card className="shadow border-0">
              <CardHeader className="bg-white py-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0 text-primary">Platform Feedbacks</h3>
                  <Badge color="primary" pill>
                    {platform_feedbacks?.length || 0} feedbacks
                  </Badge>
                </div>
              </CardHeader>

              <Container className="p-4" fluid>
                {feedbackLoader && !loadingMore ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner size="lg" color="primary" />
                  </div>
                ) : (
                  <>
                    {platform_feedbacks?.length > 0 ? (
                      <div className="feedback-list">
                        {platform_feedbacks.map((feedback, index) => (
                          <Feedback key={index} {...feedback} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="far fa-comments display-4 text-muted mb-3"></i>
                        <h4 className="text-muted">No Feedback Yet</h4>
                        <p className="text-muted">
                          Customer feedback will appear here
                        </p>
                      </div>
                    )}
                  </>
                )}

                <Row className="justify-content-center mt-4">
                  {hasMoreFeedbacks && (
                    <Button
                      color="primary"
                      size="md"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-4"
                    >
                      {loadingMore ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-chevron-down mr-2"></i>
                          Load More
                        </>
                      )}
                    </Button>
                  )}
                </Row>
              </Container>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PlatformFeedback;

const Feedback = ({
  name,
  createdAt,
  rating,
  comment,
  serviceExperience,
  restaurantName,
}) => {
  const getExperienceColor = (experience) => {
    switch (experience) {
      case "happy":
        return "success";
      case "unhappy":
        return "danger";
      case "neutral":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getExperienceIcon = (experience) => {
    switch (experience) {
      case "happy":
        return "😊 Happy";
      case "unhappy":
        return "😞 Unhappy";
      case "neutral":
        return "😐 Neutral";
      default:
        return "";
    }
  };

  return (
    <div className="feedback-card mb-4 p-4 rounded-lg shadow-sm border">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1 font-weight-bold text-dark">{restaurantName}</h5>
          <div className="d-flex align-items-center">
            <span className="text-muted mr-2">{name}</span>
            <span className="text-muted">•</span>
            <small className="text-muted ml-2">
              {createdAt &&
                moment.unix(createdAt.seconds).format("MMM DD, YYYY")}
            </small>
          </div>
        </div>
        <Badge color={getExperienceColor(serviceExperience)} pill>
          {getExperienceIcon(serviceExperience)}
        </Badge>
      </div>

      <div className="d-flex align-items-center mb-3">
        <div className="rating mr-3">
          {Array.from({ length: 5 }, (_, index) => (
            <i
              key={index}
              className={`fas fa-star ${
                index < rating ? "text-warning" : "text-light"
              }`}
            ></i>
          ))}
        </div>
        <span className="text-dark font-weight-bold">{rating}.0</span>
      </div>

      <p className="review-text mb-0 text-dark">{comment}</p>
    </div>
  );
};
